<script setup lang="ts" generic="T">
import type { SelectCustomEvent } from '@ionic/core';
import type { IonSelect } from '#build/components';

const select = ref<InstanceType<typeof IonSelect> | null>();

interface Props{
  options: T[];
  label: string;
  value: T | undefined;
  placeholder: string;
  description?: string;
  resolveKey: ((option: T) => number | string);
  optionClass?: ((option: T) => string);
}

const props = withDefaults(defineProps<Props>(), {
  description: '',
  resolvePlaceholder: undefined,
  optionClass: undefined
});

const emits = defineEmits<{
  change: [selected: T];
}>();

async function handleChange(event: SelectCustomEvent<T>) {
  emits('change', (event.detail.value));
}

function objectCompareWith(a: T, b: T) {
  return props.resolveKey(a) === props.resolveKey(b);
}

</script>

<template>
  <IonItem lines="full">
    <IonLabel>
      <div
        class="input-container"
        @click="select?.$el.click"
      >
        {{ label }}

        <div class="value-container">
          <slot name="valuePrefix" />

          <IonSelect
            ref="select"
            interface="popover"
            :placeholder="placeholder"
            :value="value"
            :compare-with="objectCompareWith"
            @ion-change="handleChange"
          >
            <IonSelectOption
              v-for="option in options"
              :key="resolveKey(option)"
              :value="option"
              :class="optionClass?.(option)"
            >
              <slot
                name="option"
                :option="option"
              >
                {{ resolveKey(option) }}
              </slot>
            </IonSelectOption>
          </IonSelect>
        </div>
      </div>
      
      <p
        v-if="description"
        class="ion-no-margin"
        @click="select?.$el.click"
      >
        {{ description }}
      </p>
    </IonLabel>
  </IonItem>
</template>

<style scoped>
.input-container{
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.value-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
</style>